<template>
  <div class="sso-verify-container">
    <div class="sso-verify-card">
      <!-- Header -->
      <div class="card-header" :style="headerStyle">
        <div class="header-icon-wrapper">
          <v-icon size="32" color="white">mdi-shield-lock-outline</v-icon>
        </div>
        <h1 class="header-title">SSO Verification</h1>
        <p class="header-subtitle">Secure Single Sign-On Authentication</p>
      </div>

      <!-- Content -->
      <div class="card-content">
        <!-- Loading State -->
        <div v-if="loading" class="state-container">
          <div class="loading-spinner">
            <v-progress-circular
              :size="72"
              :width="6"
              :color="themeColors.primary"
              indeterminate
            />
          </div>
          <h2 class="state-title">{{ statusMessage }}</h2>
          <p class="state-desc">Please wait while we verify your credentials...</p>
          <div class="progress-dots">
            <span class="dot" :style="{ '--dot-active': themeColors.primary, '--dot-base': themeColors.dotBase }"></span>
            <span class="dot" :style="{ '--dot-active': themeColors.primary, '--dot-base': themeColors.dotBase }"></span>
            <span class="dot" :style="{ '--dot-active': themeColors.primary, '--dot-base': themeColors.dotBase }"></span>
          </div>
        </div>

        <!-- Success State -->
        <div v-else-if="success" class="state-container success-state">
          <div class="state-icon success-icon">
            <v-icon size="48" color="white">mdi-check</v-icon>
          </div>
          <h2 class="state-title">{{ statusMessage }}</h2>
          <div v-if="userInfo" class="user-info">
            <div class="user-avatar">
              <v-icon size="32" color="#10b981">mdi-account-circle</v-icon>
            </div>
            <div class="user-details">
              <span class="user-name">{{ userInfo.user_name || userInfo.email }}</span>
              <span class="user-product">{{ userInfo.company_name }}</span>
            </div>
          </div>
          <p class="state-desc">You will be redirected shortly...</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="state-container error-state">
          <div class="state-icon error-icon">
            <v-icon size="48" color="white">mdi-alert</v-icon>
          </div>
          <h2 class="state-title">Verification Failed</h2>
          <p class="error-message">{{ statusMessage }}</p>
          <button class="retry-btn" :style="retryBtnStyle" @click="$router.replace('/login')">
            <v-icon left size="18">mdi-arrow-left</v-icon>
            Back to Login
          </button>
        </div>
      </div>

      <!-- Footer -->
      <div class="card-footer">
        <div class="security-badge">
          <v-icon size="14" color="#64748b">mdi-lock</v-icon>
          <span>Secured by SSO Service</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Package from "/package.json";
import { handleSsoLogin } from '@/services/loginHelper.js';

export default {
  name: "SsoVerify",
  data() {
    return {
      loading: true,
      success: false,
      error: false,
      statusMessage: "Verifying SSO token...",
      userInfo: null,
      hasRedirected: false,
      applicationVersion: Package.version,
      targetProduct: null,
    };
  },

  computed: {
    themeColors() {
      const colors = {
        'STARR': { primary: '#2A167D', secondary: '#2A167D', dotBase: '#c7d2fe' },
        'STARR_FamilyBook': { primary: '#7c3aed', secondary: '#a78bfa', dotBase: '#ddd6fe' },
        'STARR_Board': { primary: '#FFA500', secondary: '#ffbe4d', dotBase: '#fed7aa' },
      };
      return colors[this.targetProduct] || colors['STARR'];
    },
    headerStyle() {
      return {
        background: `linear-gradient(135deg, ${this.themeColors.primary} 0%, ${this.themeColors.secondary} 100%)`
      };
    },
    retryBtnStyle() {
      return {
        background: `linear-gradient(135deg, ${this.themeColors.primary} 0%, ${this.themeColors.secondary} 100%)`
      };
    }
  },

  created() {
    this.verifySsoToken();
  },

  methods: {
    closeWindow() {
      if (window && window.close) {
        window.close();
      } else {
        console.warn('window.close() is not available in this context');
      }
    },

    async verifySsoToken() {
      if (this.hasRedirected) return;

      try {
        const ssoToken = this.$route.query.sso_token;
        if (!ssoToken) throw new Error("No SSO token found");

        let domain = window.location.host.split(".");
        let prefix = domain[0];

        // Call SSO service to validate token
        const response = await this.$ssoApi.post('/api/sso/validate_token', {
          sso_token: ssoToken,
          sso_schema_name: "cmp_" + prefix,
        });

        if (!response.data.success || !response.data.valid) {
          throw new Error(response.data.error || "Invalid or expired SSO token");
        }

        const { user, source_product, target_product } = response.data;
        this.userInfo = user;
        this.targetProduct = target_product;

        // Update status message
        this.statusMessage = "Logging in...";

        // Call STARR API sso_login to get full session data
        const loginResponse = await this.$papi.post('sessions/sso_login', {
          email: user.email,
          login_id: user.login_id,
          prefix: "cmp_" + prefix,
          type: "COMPANY"
        });

        if (!loginResponse.data.token) {
          throw new Error("Failed to create session");
        }

        console.log(loginResponse, 'loginResponse');
        localStorage.clear();
        // Store SSO specific data
        localStorage.setItem("sso_source_product", source_product);
        localStorage.setItem("product", target_product);

        // Process login and redirect
        await handleSsoLogin(
          loginResponse.data,
          prefix,
          this.$router,
          this,
          this.applicationVersion,
          target_product
        );

        this.hasRedirected = true;

        // Success!
        this.loading = false;
        this.success = true;
        this.statusMessage = "Login Successful!";

      } catch (err) {
        console.error("SSO Verification Failed:", err);
        this.loading = false;
        this.error = true;
        this.statusMessage = err.message || "SSO verification failed";
      }
    },
  },
};
</script>

<style scoped>
.sso-verify-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding: 20px;
}

.sso-verify-card {
  width: 100%;
  max-width: 440px;
  background: #fff;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

/* Header */
.card-header {
  padding: 32px 24px;
  text-align: center;
}

.header-icon-wrapper {
  width: 72px;
  height: 72px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  backdrop-filter: blur(10px);
}

.header-title {
  font-size: 24px;
  font-weight: 700;
  color: #fff;
  margin: 0 0 4px;
}

.header-subtitle {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
}

/* Content */
.card-content {
  padding: 40px 32px;
}

.state-container {
  text-align: center;
}

.loading-spinner {
  margin-bottom: 24px;
}

.state-title {
  font-size: 20px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 8px;
}

.state-desc {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

/* Progress Dots Animation */
.progress-dots {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 20px;
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--dot-base, #c7d2fe);
  animation: dotPulse 1.4s infinite ease-in-out;
}

.dot:nth-child(1) { animation-delay: 0s; }
.dot:nth-child(2) { animation-delay: 0.2s; }
.dot:nth-child(3) { animation-delay: 0.4s; }

@keyframes dotPulse {
  0%, 80%, 100% {
    transform: scale(1);
    background: var(--dot-base, #c7d2fe);
  }
  40% {
    transform: scale(1.3);
    background: var(--dot-active, #6366f1);
  }
}

/* Success State */
.success-state .state-title {
  color: #10b981;
}

.state-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
}

.success-icon {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  box-shadow: 0 8px 24px rgba(16, 185, 129, 0.3);
}

.user-info {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  border-radius: 12px;
  padding: 16px 20px;
  margin: 20px 0;
}

.user-avatar {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.user-details {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.user-name {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
}

.user-product {
  font-size: 13px;
  color: #64748b;
}

/* Error State */
.error-state .state-title {
  color: #ef4444;
}

.error-icon {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  box-shadow: 0 8px 24px rgba(239, 68, 68, 0.3);
}

.error-message {
  font-size: 14px;
  color: #64748b;
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  border-radius: 10px;
  padding: 14px 18px;
  margin: 16px 0 24px;
}

.retry-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.retry-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(42, 22, 125, 0.4);
}

/* Footer */
.card-footer {
  padding: 16px 24px;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
}

.security-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 12px;
  color: #64748b;
}

/* Responsive */
@media (max-width: 480px) {
  .sso-verify-card {
    border-radius: 16px;
  }

  .card-header {
    padding: 24px 20px;
  }

  .header-icon-wrapper {
    width: 60px;
    height: 60px;
  }

  .header-title {
    font-size: 20px;
  }

  .card-content {
    padding: 32px 24px;
  }

  .state-icon {
    width: 64px;
    height: 64px;
  }
}
</style>
